import {
  compareIndex,
  keyBetween,
  keysBetween,
  type BoardMeta,
  type BoardNode,
  type CallChatMessage,
  nanoid,
  type CommentMessage,
  type NodeId,
  type Presence,
  type UserId,
} from '@kanvas/board-core';
import { IndexeddbPersistence } from 'y-indexeddb';
import { Awareness } from 'y-protocols/awareness';
import { WebsocketProvider } from 'y-websocket';
import * as Y from 'yjs';

/** Transactions tagged with this origin are the ones local undo can revert. */
export const LOCAL_ORIGIN = Symbol('kanvas-local');

/** Default shared timer length when a board has never set one. */
export const DEFAULT_TIMER_MS = 45 * 60 * 1000;

/** How many chat messages a board keeps — see the chat section below. */
const CALL_CHAT_LIMIT = 500;

/** Cap local undo history so long sessions do not retain every edit in RAM. */
const UNDO_STACK_LIMIT = 100;

/** How often `lastActive` may bump without any other presence field changing. */
const LAST_ACTIVE_MS = 2000;

/**
 * Link-based access, the same idea as a Google Docs share link: the role
 * lives in the URL (`?role=comment` / `?role=view`), not behind a login, so
 * "editor" stays the default for every link created before this existed.
 */
export type BoardRole = 'editor' | 'commenter' | 'viewer';

export interface BoardDocIdentity {
  userId: string;
  name: string;
  color: string;
  avatar?: string | null;
}

export interface BoardDocOptions {
  boardId: string;
  /** Absolute or protocol-relative websocket endpoint. */
  wsUrl?: string;
  connect?: boolean;
  role?: BoardRole;
  /**
   * Published into awareness immediately so peers see this client in the
   * participants list before React mounts the canvas.
   */
  identity?: BoardDocIdentity;
}

export type DocListener = () => void;

export interface ConnectionState {
  /** `denied`: the server refused or removed this client; no reconnecting. */
  status: 'connecting' | 'connected' | 'disconnected' | 'offline' | 'denied';
  peers: number;
}

/** Nodes that appeared or disappeared in a single transaction. */
export interface StructureChange {
  added: BoardNode[];
  removed: BoardNode[];
  /** False when the change arrived from a peer over the network. */
  local: boolean;
}

/**
 * Thin, typed façade over the Yjs document.
 *
 * The rest of the app never touches Y types directly. That boundary matters:
 * it keeps CRDT concerns (transactions, origins, undo scope) in one file, and
 * it lets the renderer work with plain sorted arrays of nodes.
 */
export class BoardDoc {
  readonly doc: Y.Doc;
  readonly nodes: Y.Map<BoardNode>;
  readonly meta: Y.Map<unknown>;
  readonly comments: Y.Array<CommentMessage>;
  /** In-call chat, kept in the document so history survives leaving the call. */
  readonly callChat: Y.Array<CallChatMessage>;
  /** Dot-voting ballots, keyed by userId so concurrent votes never conflict. */
  readonly votes: Y.Map<Record<NodeId, number>>;
  readonly undoManager: Y.UndoManager;
  readonly awareness: Awareness;
  readonly boardId: string;
  readonly role: BoardRole;

  provider: WebsocketProvider | null = null;
  private readonly idb: IndexeddbPersistence;
  /**
   * Resolves once the doc has loaded whatever this browser had cached for
   * this board — before the websocket even gets a chance to connect. That is
   * what makes the board usable offline: opening it never blocks on the
   * network, and anything typed while offline is sitting in IndexedDB the
   * moment it happens, not just in memory.
   */
  readonly offlineReady: Promise<void>;

  /**
   * When the server first handed over the board's existing content, or null
   * while that is still outstanding.
   *
   * The renderer needs it to tell a board *arriving* from a change someone
   * just made: the initial sync lands as one batch of remote additions, and
   * animating those would replay the entire board as if it were being drawn
   * from scratch in front of the person who just opened it.
   */
  syncedAt: number | null = null;

  private sortedCache: BoardNode[] | null = null;
  /** Bumped on every document change; React subscribes to this, not to Y. */
  private version = 0;
  /** Bumped on any awareness packet (cursors, drafts, lasers). */
  private presenceVersion = 0;
  /**
   * Bumped only when the participant roster changes (join/leave/identity).
   * React chrome like the avatar stack must not re-render on every cursor tick.
   */
  private rosterVersion = 0;
  private rosterFingerprint = '';
  private listeners = new Set<DocListener>();
  private presenceListeners = new Set<DocListener>();
  private rosterListeners = new Set<DocListener>();
  private connectionListeners = new Set<(s: ConnectionState) => void>();
  private structureListeners = new Set<(change: StructureChange) => void>();
  private connection: ConnectionState = { status: 'offline', peers: 0 };
  /** Coalesces CRDT storms (IndexedDB hydrate + WS sync) into one React tick. */
  private emitFrame = 0;

  constructor(options: BoardDocOptions) {
    this.boardId = options.boardId;
    this.role = options.role ?? 'editor';
    this.doc = new Y.Doc({ guid: options.boardId });
    this.nodes = this.doc.getMap<BoardNode>('nodes');
    this.meta = this.doc.getMap('meta');
    this.comments = this.doc.getArray<CommentMessage>('comments');
    this.callChat = this.doc.getArray<CallChatMessage>('callChat');
    this.votes = this.doc.getMap('votes');

    this.undoManager = new Y.UndoManager([this.nodes, this.meta], {
      trackedOrigins: new Set([LOCAL_ORIGIN]),
      captureTimeout: 400,
    });
    this.undoManager.on('stack-item-added', () => {
      const stack = this.undoManager.undoStack;
      while (stack.length > UNDO_STACK_LIMIT) stack.shift();
    });

    this.nodes.observeDeep((events, transaction) => {
      this.sortedCache = null;
      this.collectStructureChange(events, transaction);
      this.emit();
    });
    this.meta.observe(() => this.emit());
    this.comments.observe(() => this.emit());
    this.callChat.observe(() => this.emit());
    this.votes.observe(() => this.emit());

    this.awareness = new Awareness(this.doc);
    this.awareness.on(
      'change',
      (changes: { added: number[]; updated: number[]; removed: number[] }) => {
        // Local cursor / viewport / draft packets must not wake every presence
        // subscriber. The renderer paints peers from getPresence() (self is
        // excluded) and paints our own ink/laser from scratch with its own
        // invalidate — so a pen hovering over the tablet used to force a full
        // board repaint ~20×/s for nothing the canvas would show.
        const localId = this.doc.clientID;
        const remoteChanged =
          changes.added.some((id) => id !== localId) ||
          changes.updated.some((id) => id !== localId) ||
          changes.removed.some((id) => id !== localId);
        if (remoteChanged) {
          this.presenceVersion++;
          for (const l of this.presenceListeners) l();
        }

        const fingerprint = this.computeRosterFingerprint();
        if (fingerprint !== this.rosterFingerprint) {
          this.rosterFingerprint = fingerprint;
          this.rosterVersion++;
          for (const l of this.rosterListeners) l();
        }

        this.setConnection({ ...this.connection, peers: this.awareness.getStates().size });
      },
    );

    // Namespaced so a browser that has visited several boards keeps them in
    // separate IndexedDB object stores rather than one growing document.
    this.idb = new IndexeddbPersistence(`kanvas:${this.boardId}`, this.doc);
    this.offlineReady = this.idb.whenSynced.then(() => undefined);

    // Identity must land before the websocket handshake finishes syncing
    // awareness, otherwise peers filter this client out until React mounts.
    if (options.identity) {
      this.setPresence({
        userId: options.identity.userId,
        name: options.identity.name,
        color: options.identity.color,
        avatar: options.identity.avatar ?? null,
        cursor: null,
        selection: [],
        viewport: null,
        tool: 'select',
      });
    }

    if (options.connect !== false) this.connect(options.wsUrl);
  }

  // -------------------------------------------------------------------------
  // Connection
  // -------------------------------------------------------------------------

  connect(wsUrl?: string): void {
    if (this.provider) return;
    const url = wsUrl ?? defaultWsUrl();

    this.provider = new WebsocketProvider(url, this.boardId, this.doc, {
      awareness: this.awareness,
      connect: true,
      // Reconnect quickly on a flaky network but back off if the server is down.
      maxBackoffTime: 8000,
    });

    this.setConnection({ status: 'connecting', peers: this.connection.peers });

    // Fires after sync step 2, i.e. once this client holds everything the
    // server had. A reconnect syncs again; the first one is the one that
    // matters, so later ones are ignored.
    this.provider.on('sync', (isSynced: boolean) => {
      if (isSynced && this.syncedAt === null) this.syncedAt = performance.now();
    });

    // Kicked, banned, access revoked (4003) or refused at the door (1008):
    // reconnecting would either be refused again or, on an org-open board,
    // quietly undo the kick. 4001 (role changed) reconnects as usual.
    this.provider.on('connection-close', (event: CloseEvent | null) => {
      if (!event || (event.code !== 4003 && event.code !== 1008)) return;
      if (this.provider) this.provider.shouldConnect = false;
      this.setConnection({ status: 'denied', peers: 0 });
    });

    this.provider.on('status', (event: { status: string }) => {
      if (this.connection.status === 'denied') return;
      this.setConnection({
        status:
          event.status === 'connected'
            ? 'connected'
            : event.status === 'connecting'
              ? 'connecting'
              : 'disconnected',
        peers: this.awareness.getStates().size,
      });
    });
  }

  disconnect(): void {
    this.provider?.disconnect();
    this.provider?.destroy();
    this.provider = null;
    this.setConnection({ status: 'offline', peers: 0 });
  }

  private setConnection(next: ConnectionState): void {
    const changed = next.status !== this.connection.status || next.peers !== this.connection.peers;
    this.connection = next;
    if (changed) for (const l of this.connectionListeners) l(next);
  }

  getConnection(): ConnectionState {
    return this.connection;
  }

  onConnectionChange(listener: (s: ConnectionState) => void): () => void {
    this.connectionListeners.add(listener);
    return () => this.connectionListeners.delete(listener);
  }

  // -------------------------------------------------------------------------
  // Subscriptions
  // -------------------------------------------------------------------------

  subscribe(listener: DocListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  subscribePresence(listener: DocListener): () => void {
    this.presenceListeners.add(listener);
    return () => this.presenceListeners.delete(listener);
  }

  /** Join/leave/rename only — not cursor or viewport motion. */
  subscribeRoster(listener: DocListener): () => void {
    this.rosterListeners.add(listener);
    return () => this.rosterListeners.delete(listener);
  }

  /**
   * Notifies when nodes appear or disappear, and whether the change came from
   * this client or a peer.
   *
   * The renderer needs this to animate arrivals and departures. It cannot infer
   * it from the plain change notification, which only says "something changed",
   * and it must not diff the whole node list every frame just to find out.
   */
  onStructureChange(listener: (change: StructureChange) => void): () => void {
    this.structureListeners.add(listener);
    return () => this.structureListeners.delete(listener);
  }

  private collectStructureChange(
    events: Y.YEvent<Y.AbstractType<unknown>>[],
    transaction: Y.Transaction,
  ): void {
    if (this.structureListeners.size === 0) return;

    const added: BoardNode[] = [];
    const removed: BoardNode[] = [];

    for (const event of events) {
      if (event.target !== this.nodes) continue;
      event.changes.keys.forEach((change, key) => {
        if (change.action === 'add') {
          const node = this.nodes.get(key);
          if (node) added.push(node);
        } else if (change.action === 'delete') {
          const old = change.oldValue as BoardNode | undefined;
          if (old && typeof old === 'object' && 'type' in old) removed.push(old);
        }
      });
    }

    if (added.length === 0 && removed.length === 0) return;

    // `local` is true for anything this client did, including its own live
    // gesture writes; `transaction.local` covers both origins we use.
    const change: StructureChange = { added, removed, local: transaction.local };
    for (const listener of this.structureListeners) listener(change);
  }

  private emit(): void {
    // IndexedDB hydrate and the first WS sync can each fire hundreds of map
    // updates back-to-back. Coalescing to one frame keeps React from rebuilding
    // the whole editor shell on every CRDT key.
    if (this.emitFrame) return;
    this.emitFrame = requestAnimationFrame(() => {
      this.emitFrame = 0;
      this.version++;
      for (const l of this.listeners) l();
    });
  }

  /** Monotonic document version, for useSyncExternalStore snapshots. */
  getVersion = (): number => this.version;

  getPresenceVersion = (): number => this.presenceVersion;

  getRosterVersion = (): number => this.rosterVersion;

  /** Cheap emptiness check — avoids sorting the full node list for chrome. */
  isEmpty(): boolean {
    return this.nodes.size === 0;
  }

  private computeRosterFingerprint(): string {
    const parts: string[] = [];
    this.awareness.getStates().forEach((state, clientId) => {
      if (clientId === this.doc.clientID) return;
      const p = state as Partial<Presence>;
      if (!p.userId) return;
      parts.push(
        [
          p.userId,
          p.name ?? '',
          p.color ?? '',
          p.avatar ?? '',
          p.inCall ? '1' : '0',
          p.presenting ? '1' : '0',
          p.micOn ? '1' : '0',
          p.camOn ? '1' : '0',
        ].join('\0'),
      );
    });
    parts.sort();
    return parts.join('\n');
  }

  // -------------------------------------------------------------------------
  // Reads
  // -------------------------------------------------------------------------

  get(id: NodeId): BoardNode | undefined {
    return this.nodes.get(id);
  }

  lookup = (id: NodeId): BoardNode | undefined => this.nodes.get(id);

  /** Nodes in paint order. Cached, invalidated on any document change. */
  all(): BoardNode[] {
    if (this.sortedCache) return this.sortedCache;
    const out: BoardNode[] = [];
    this.nodes.forEach((n) => out.push(n));
    out.sort(compareIndex);
    this.sortedCache = out;
    return out;
  }

  getMany(ids: readonly NodeId[]): BoardNode[] {
    const out: BoardNode[] = [];
    for (const id of ids) {
      const n = this.nodes.get(id);
      if (n) out.push(n);
    }
    return out;
  }

  getMeta(): BoardMeta {
    return {
      id: this.boardId,
      name: (this.meta.get('name') as string) ?? 'Без названия',
      createdAt: (this.meta.get('createdAt') as number) ?? Date.now(),
      updatedAt: (this.meta.get('updatedAt') as number) ?? Date.now(),
      background: (this.meta.get('background') as string) ?? '#F5F6F8',
      grid: (this.meta.get('grid') as BoardMeta['grid']) ?? 'lines',
      snapToGrid: (this.meta.get('snapToGrid') as boolean) ?? false,
      gridSize: (this.meta.get('gridSize') as number) ?? 20,
    };
  }

  /** Full read/write: create, move, delete and change board-wide settings. */
  canEdit(): boolean {
    return this.role === 'editor';
  }

  /** Can add pins, reply in threads and cast votes, but not touch content. */
  canComment(): boolean {
    return this.role !== 'viewer';
  }

  setMeta(patch: Partial<BoardMeta>): void {
    if (!this.canEdit()) return;
    this.transact(() => {
      for (const [k, v] of Object.entries(patch)) this.meta.set(k, v);
      this.meta.set('updatedAt', Date.now());
    });
  }

  /**
   * Align the live document title with the server board record.
   *
   * The dashboard renames the API row; the top bar reads Yjs `meta.name`.
   * Without this, a board renamed (or created with a real title) still shows
   * the empty-document default «Без названия» until someone edits the title
   * inside the board.
   */
  syncNameFromServer(serverName: string): void {
    const name = serverName.trim() || 'Без названия';
    const current = (this.meta.get('name') as string | undefined)?.trim();
    if (current === name) return;
    // Viewers cannot write; they still see `boardName` from the API in the UI.
    if (!this.canEdit()) return;
    this.transact(() => {
      this.meta.set('name', name);
      this.meta.set('updatedAt', Date.now());
    }, 'ephemeral');
  }

  // -------------------------------------------------------------------------
  // Timer
  //
  // A shared countdown, useful for timeboxing a workshop activity. It rides
  // the same `meta` map as the board settings but under the `ephemeral`
  // origin, so starting or resetting the timer is never something Ctrl+Z
  // undoes — it is session state, not document content.
  // -------------------------------------------------------------------------

  getTimer(): { running: boolean; endAt: number | null; durationMs: number } {
    return {
      running: (this.meta.get('timerRunning') as boolean) ?? false,
      endAt: (this.meta.get('timerEndAt') as number | null) ?? null,
      durationMs: (this.meta.get('timerDurationMs') as number) ?? DEFAULT_TIMER_MS,
    };
  }

  setTimer(patch: { running?: boolean; endAt?: number | null; durationMs?: number }): void {
    if (!this.canEdit()) return;
    this.transact(() => {
      if (patch.running !== undefined) this.meta.set('timerRunning', patch.running);
      if (patch.endAt !== undefined) this.meta.set('timerEndAt', patch.endAt);
      if (patch.durationMs !== undefined) this.meta.set('timerDurationMs', patch.durationMs);
    }, 'ephemeral');
  }

  // -------------------------------------------------------------------------
  // Dot voting
  //
  // Ballots are one entry per user (`votes.get(userId)`), so two people
  // voting at the same instant touch different keys and never conflict —
  // the same reason presence is per-user rather than one shared blob.
  // -------------------------------------------------------------------------

  getVoteSession(): { active: boolean; max: number; prompt: string } {
    return {
      active: (this.meta.get('voteActive') as boolean) ?? false,
      max: (this.meta.get('voteMax') as number) ?? 3,
      prompt: (this.meta.get('votePrompt') as string) ?? '',
    };
  }

  setVoteSession(patch: { active?: boolean; max?: number; prompt?: string }): void {
    if (!this.canEdit()) return;
    this.transact(() => {
      if (patch.active !== undefined) this.meta.set('voteActive', patch.active);
      if (patch.max !== undefined) this.meta.set('voteMax', patch.max);
      if (patch.prompt !== undefined) this.meta.set('votePrompt', patch.prompt);
    }, 'ephemeral');
  }

  getVotesFor(userId: UserId): Record<NodeId, number> {
    return this.votes.get(userId) ?? {};
  }

  votesUsedBy(userId: UserId): number {
    const ballot = this.votes.get(userId) ?? {};
    return Object.values(ballot).reduce((sum, n) => sum + n, 0);
  }

  /** Totals across every voter, for the results view and the on-canvas badges. */
  voteTotals(): Map<NodeId, number> {
    const totals = new Map<NodeId, number>();
    this.votes.forEach((ballot) => {
      for (const [nodeId, count] of Object.entries(ballot)) {
        totals.set(nodeId, (totals.get(nodeId) ?? 0) + count);
      }
    });
    return totals;
  }

  castVote(userId: UserId, nodeId: NodeId, delta: 1 | -1): void {
    if (!this.canComment()) return;
    this.transact(() => {
      const ballot = { ...(this.votes.get(userId) ?? {}) };
      const next = Math.max(0, (ballot[nodeId] ?? 0) + delta);
      if (next === 0) delete ballot[nodeId];
      else ballot[nodeId] = next;
      this.votes.set(userId, ballot);
    }, 'ephemeral');
  }

  clearVotes(): void {
    if (!this.canEdit()) return;
    this.transact(() => {
      this.votes.forEach((_, key) => this.votes.delete(key));
    }, 'ephemeral');
  }

  // -------------------------------------------------------------------------
  // In-call chat
  //
  // Chat rides the board document rather than the LiveKit data channel so it
  // outlives the call: someone who joins late, drops out, or reads it after
  // hanging up still sees what was said. It is capped because a document is
  // synced in full to every new client, and an unbounded transcript would
  // eventually make opening the board slower for everyone.
  // -------------------------------------------------------------------------

  /** Messages in send order, optionally narrowed to one call. */
  getCallChat(callId?: string | null): CallChatMessage[] {
    // A reaction rewrites its message (delete + insert). Two people reacting
    // to the same message at once each insert a copy, so the same id can
    // appear twice; keep the later copy in place of the first.
    const byId = new Map<string, CallChatMessage>();
    for (const m of this.callChat.toArray()) byId.set(m.id, m);
    const all = [...byId.values()];
    return callId ? all.filter((m) => m.callId === callId) : all;
  }

  sendCallChat(message: Omit<CallChatMessage, 'id' | 'ts' | 'reactions'>): void {
    const text = message.text.trim();
    const imageUrl = message.imageUrl?.trim() || null;
    if ((!text && !imageUrl) || !this.canComment()) return;
    this.transact(() => {
      this.callChat.push([
        {
          ...message,
          text,
          imageUrl,
          imageMime: imageUrl ? (message.imageMime ?? null) : null,
          reactions: {},
          id: `cm_${nanoid(12)}`,
          ts: Date.now(),
        },
      ]);
      const overflow = this.callChat.length - CALL_CHAT_LIMIT;
      if (overflow > 0) this.callChat.delete(0, overflow);
    }, 'ephemeral');
  }

  /** Toggle one emoji on a message for this user. Empty sets are dropped. */
  toggleCallChatReaction(messageId: string, emoji: string, userId: string): void {
    if (!this.canComment()) return;
    const all = this.callChat.toArray();
    const idx = all.findIndex((m) => m.id === messageId);
    if (idx < 0) return;
    const msg = all[idx]!;
    const reactions = { ...(msg.reactions ?? {}) };
    const set = new Set(reactions[emoji] ?? []);
    if (set.has(userId)) set.delete(userId);
    else set.add(userId);
    if (set.size === 0) delete reactions[emoji];
    else reactions[emoji] = [...set];
    this.transact(() => {
      this.callChat.delete(idx, 1);
      this.callChat.insert(idx, [{ ...msg, reactions }]);
    }, 'ephemeral');
  }

  /** Highest and lowest z-order keys, for bring-to-front / send-to-back. */
  indexRange(): { min: string | null; max: string | null } {
    const all = this.all();
    if (all.length === 0) return { min: null, max: null };
    return { min: all[0]!.index, max: all[all.length - 1]!.index };
  }

  /** A z-order key that puts a new node above everything. */
  topIndex(): string {
    return keyBetween(this.indexRange().max, null);
  }

  topIndices(count: number): string[] {
    return keysBetween(this.indexRange().max, null, count);
  }

  // -------------------------------------------------------------------------
  // Writes
  // -------------------------------------------------------------------------

  /** All mutations funnel through here so undo scoping stays correct. */
  transact<T>(fn: () => T, origin: unknown = LOCAL_ORIGIN): T {
    return this.doc.transact(fn, origin);
  }

  /** A commenter may only add comment pins; everything else needs `editor`. */
  add(node: BoardNode): void {
    if (this.role === 'viewer') return;
    if (this.role === 'commenter' && node.type !== 'comment') return;
    this.transact(() => this.nodes.set(node.id, node));
  }

  addMany(nodes: readonly BoardNode[]): void {
    if (!this.canEdit()) return;
    this.transact(() => {
      for (const n of nodes) this.nodes.set(n.id, n);
    });
  }

  /**
   * Shallow-merges a patch into a node. Nodes are immutable snapshots inside
   * the map, so an update replaces the whole value; Yjs still merges concurrent
   * updates to *different* nodes cleanly, and last-write-wins on the same node
   * is the behaviour users expect from a whiteboard.
   */
  update<T extends BoardNode>(id: NodeId, patch: Partial<T>): void {
    if (this.role === 'viewer') return;
    this.transact(() => {
      const current = this.nodes.get(id);
      if (!current) return;
      // A commenter's write access is scoped to comment pins — resolving or
      // editing their own thread marker — never board content.
      if (this.role === 'commenter' && current.type !== 'comment') return;
      this.nodes.set(id, { ...current, ...patch, updatedAt: Date.now() } as BoardNode);
    });
  }

  updateMany(patches: ReadonlyMap<NodeId, Partial<BoardNode>>): void {
    if (!this.canEdit()) return;
    this.transact(() => {
      const now = Date.now();
      patches.forEach((patch, id) => {
        const current = this.nodes.get(id);
        if (!current) return;
        this.nodes.set(id, { ...current, ...patch, updatedAt: now } as BoardNode);
      });
    });
  }

  /**
   * Same as updateMany but not tracked by undo — used for live drag previews so
   * a single gesture produces exactly one undo entry when it commits.
   */
  updateManyEphemeral(patches: ReadonlyMap<NodeId, Partial<BoardNode>>): void {
    if (!this.canEdit()) return;
    this.transact(() => {
      patches.forEach((patch, id) => {
        const current = this.nodes.get(id);
        if (!current) return;
        this.nodes.set(id, { ...current, ...patch } as BoardNode);
      });
    }, 'ephemeral');
  }

  /**
   * Commits a drag so it lands as exactly one undo step.
   *
   * Live gesture feedback is written with the `ephemeral` origin, which the
   * UndoManager ignores — peers see the movement, but no undo entry is created
   * per frame. That leaves a problem at the end: the last tracked state is
   * already the dragged state, so undo would be a no-op. So the commit first
   * rewinds to the pre-gesture values (still untracked), then writes the final
   * values in a tracked transaction. The undo entry then spans the whole drag.
   */
  commitGesture(
    originals: ReadonlyMap<NodeId, Partial<BoardNode>>,
    finals: ReadonlyMap<NodeId, Partial<BoardNode>>,
    /**
     * Follow-up writes that belong to the same user action (re-parenting
     * into the frame the drag ended over). They run inside the tracked
     * transaction, so one Ctrl+Z undoes the move and the re-parenting
     * together; done afterwards they became a second, invisible undo step.
     */
    alsoInSameStep?: () => void,
  ): void {
    if (!this.canEdit() || finals.size === 0) return;
    this.transact(() => {
      originals.forEach((patch, id) => {
        const current = this.nodes.get(id);
        if (current) this.nodes.set(id, { ...current, ...patch } as BoardNode);
      });
    }, 'ephemeral');

    this.transact(() => {
      const now = Date.now();
      finals.forEach((patch, id) => {
        const current = this.nodes.get(id);
        if (current) this.nodes.set(id, { ...current, ...patch, updatedAt: now } as BoardNode);
      });
      alsoInSameStep?.();
    });
    this.undoManager.stopCapturing();
  }

  /** Ends the current undo capture window, so the next edit starts a new step. */
  breakUndoBatch(): void {
    this.undoManager.stopCapturing();
  }

  remove(ids: readonly NodeId[]): void {
    if (this.role === 'viewer') return;
    // A commenter may only remove their own comment pins, never board content.
    const allowedIds =
      this.role === 'commenter' ? ids.filter((id) => this.nodes.get(id)?.type === 'comment') : ids;
    if (allowedIds.length === 0) return;
    this.transact(() => {
      const doomed = new Set(allowedIds);

      // Cascade: group children, frame children and any connector attached to a
      // removed node go with it, otherwise the board keeps dangling references.
      for (const id of allowedIds) {
        const node = this.nodes.get(id);
        if (node?.type === 'group') for (const c of node.children) doomed.add(c);
      }
      this.nodes.forEach((n) => {
        if (n.parentId && doomed.has(n.parentId) && n.type !== 'frame') doomed.add(n.id);
        if (n.type === 'connector') {
          const a = n.start.kind === 'node' && doomed.has(n.start.nodeId);
          const b = n.end.kind === 'node' && doomed.has(n.end.nodeId);
          if (a || b) doomed.add(n.id);
        }
      });

      for (const id of doomed) this.nodes.delete(id);
    });
  }

  // -------------------------------------------------------------------------
  // Z-order
  // -------------------------------------------------------------------------

  reorder(ids: readonly NodeId[], target: 'front' | 'back' | 'forward' | 'backward'): void {
    if (!this.canEdit()) return;
    const all = this.all();
    if (all.length === 0 || ids.length === 0) return;
    const moving = new Set(ids);
    const selected = all.filter((n) => moving.has(n.id));
    if (selected.length === 0) return;

    this.transact(() => {
      if (target === 'front') {
        const keys = keysBetween(all[all.length - 1]!.index, null, selected.length);
        selected.forEach((n, i) => this.nodes.set(n.id, { ...n, index: keys[i]! }));
        return;
      }
      if (target === 'back') {
        const keys = keysBetween(null, all[0]!.index, selected.length);
        selected.forEach((n, i) => this.nodes.set(n.id, { ...n, index: keys[i]! }));
        return;
      }

      const step = target === 'forward' ? 1 : -1;
      // Walk in the direction of travel so a multi-selection keeps its order.
      const ordered = step === 1 ? [...selected].reverse() : selected;
      for (const node of ordered) {
        const list = this.all();
        const at = list.findIndex((n) => n.id === node.id);
        const neighbourAt = at + step;
        if (neighbourAt < 0 || neighbourAt >= list.length) continue;
        const neighbour = list[neighbourAt]!;
        if (moving.has(neighbour.id)) continue;
        const beyond = list[neighbourAt + step];
        const index =
          step === 1
            ? keyBetween(neighbour.index, beyond?.index ?? null)
            : keyBetween(beyond?.index ?? null, neighbour.index);
        this.nodes.set(node.id, { ...node, index });
        this.sortedCache = null;
      }
    });
  }

  // -------------------------------------------------------------------------
  // Presence
  // -------------------------------------------------------------------------

  setPresence(patch: Partial<Presence>): void {
    const current = (this.awareness.getLocalState() ?? {}) as Partial<Presence>;
    const merged: Partial<Presence> = { ...current, ...patch };
    // Awareness replaces the whole local blob on every write, so no-op patches
    // (same cursor, same selection) still hit the wire and every peer's React
    // roster. Skip when nothing visible changed.
    if (presenceContentEqual(current, merged)) return;

    const now = Date.now();
    const lastActive =
      !current.lastActive || now - current.lastActive >= LAST_ACTIVE_MS
        ? now
        : current.lastActive;
    this.awareness.setLocalState({ ...merged, lastActive });
  }

  getPresence(): Presence[] {
    const out: Presence[] = [];
    this.awareness.getStates().forEach((state, clientId) => {
      if (clientId === this.doc.clientID) return;
      const p = state as Partial<Presence>;
      if (!p.userId) return;
      out.push({
        userId: p.userId,
        name: p.name ?? 'Гость',
        color: p.color ?? '#888',
        avatar: p.avatar ?? null,
        cursor: p.cursor ?? null,
        selection: p.selection ?? [],
        viewport: p.viewport ?? null,
        tool: p.tool ?? 'select',
        laser: p.laser ?? null,
        draft: p.draft ?? null,
        speaking: p.speaking ?? false,
        micOn: p.micOn ?? false,
        camOn: p.camOn ?? false,
        inCall: p.inCall ?? false,
        presenting: p.presenting ?? false,
        lastActive: p.lastActive ?? 0,
      });
    });
    return out;
  }

  // -------------------------------------------------------------------------
  // Snapshots
  // -------------------------------------------------------------------------

  /** Full document snapshot, for export and for seeding a duplicate board. */
  exportJSON(): { meta: BoardMeta; nodes: BoardNode[]; comments: CommentMessage[] } {
    return {
      meta: this.getMeta(),
      nodes: this.all(),
      comments: this.comments.toArray(),
    };
  }

  importJSON(payload: { nodes: BoardNode[]; meta?: Partial<BoardMeta> }): void {
    if (!this.canEdit()) return;
    this.transact(() => {
      for (const node of payload.nodes) this.nodes.set(node.id, node);
      if (payload.meta) for (const [k, v] of Object.entries(payload.meta)) this.meta.set(k, v);
    });
  }

  /** Drops this browser's cached copy — used when a board is deleted locally. */
  async clearOfflineCache(): Promise<void> {
    await this.idb.clearData();
  }

  destroy(): void {
    if (this.emitFrame) {
      cancelAnimationFrame(this.emitFrame);
      this.emitFrame = 0;
    }
    this.disconnect();
    this.undoManager.destroy();
    this.awareness.destroy();
    void this.idb.destroy();
    this.doc.destroy();
    this.listeners.clear();
    this.presenceListeners.clear();
    this.rosterListeners.clear();
    this.connectionListeners.clear();
    this.structureListeners.clear();
  }
}

function defaultWsUrl(): string {
  if (typeof window === 'undefined') return 'ws://localhost:8787/collab';
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/collab`;
}

function sameSelection(a: string[] | undefined, b: string[] | undefined): boolean {
  const left = a ?? [];
  const right = b ?? [];
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i++) if (left[i] !== right[i]) return false;
  return true;
}

function sameViewport(
  a: Presence['viewport'] | undefined,
  b: Presence['viewport'] | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}

function sameCursor(
  a: Presence['cursor'] | undefined,
  b: Presence['cursor'] | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.x === b.x && a.y === b.y;
}

/** Fields peers actually render — ignores `lastActive` churn. */
function presenceContentEqual(a: Partial<Presence>, b: Partial<Presence>): boolean {
  return (
    a.userId === b.userId &&
    a.name === b.name &&
    a.color === b.color &&
    a.avatar === b.avatar &&
    a.tool === b.tool &&
    a.speaking === b.speaking &&
    a.micOn === b.micOn &&
    a.camOn === b.camOn &&
    a.inCall === b.inCall &&
    a.presenting === b.presenting &&
    a.draft === b.draft &&
    a.laser === b.laser &&
    sameCursor(a.cursor, b.cursor) &&
    sameSelection(a.selection, b.selection) &&
    sameViewport(a.viewport, b.viewport)
  );
}
